import React, { useCallback, useState } from 'react';
import { Text } from '@moda/om';
import { reportError } from '../../../lib/reportError';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { usePreviewOrderShippingAddressUpdate } from '../../../hooks/useUpdateOrderShippingAddress';
import { OrderDetailsShippingAddressConfirmation } from '../../ShippingAddress/OrderDetailsShippingAddress/OrderDetailsShippingAddressConfirmation';
import { PaymentSource } from '../../../constants';
import { useOrderDetails } from '../../OrderDetails';
import { useOrderDetailsPaymentSource } from '../../OrderDetailsPaymentSources';
import { AmazonShippingAndPayment } from '../AmazonShippingAndPayment';

enum Mode {
  Resting,
  Saving,
  Switching
}

export const OrderDetailsAmazonShippingAndPayment: React.FC = () => {
  const [mode, setMode] = useState(Mode.Resting);
  const { orderSummary, orderNumber } = useOrderDetails();
  const { showErrorModal } = useErrorModal();
  const {
    changePaymentSource,
    selectLatestPaymentMethod,
    selectLatestAddress,
    getSelectLatestAddressValidation
  } = useOrderDetailsPaymentSource();
  const { previewOrderShippingAddressUpdate } = usePreviewOrderShippingAddressUpdate(orderNumber);

  const onSwitchToAdyen = useCallback(() => changePaymentSource(PaymentSource.Adyen), [
    changePaymentSource
  ]);

  const onSubmit = useCallback(async () => {
    const { canSelectLatestAddress, latestAddress } = getSelectLatestAddressValidation();
    if (canSelectLatestAddress && latestAddress) {
      try {
        await previewOrderShippingAddressUpdate(latestAddress.id);
        setMode(Mode.Switching);
      } catch (error) {
        showErrorModal();
        reportError(error);
      }
    } else {
      await selectLatestPaymentMethod();
      await selectLatestAddress();
    }
  }, [
    getSelectLatestAddressValidation,
    previewOrderShippingAddressUpdate,
    selectLatestPaymentMethod,
    selectLatestAddress,
    showErrorModal
  ]);

  const onClickProceed = useCallback(async () => {
    setMode(Mode.Saving);
    await selectLatestPaymentMethod();
    await selectLatestAddress();
    setMode(Mode.Resting);
  }, [selectLatestPaymentMethod, selectLatestAddress]);

  if (!orderSummary) return null;

  return (
    <>
      <Text family="serif" treatment="h5">
        Shipping &amp; Payment
      </Text>
      <AmazonShippingAndPayment
        theme="box"
        address={{
          __typename: 'RestAddress',
          id: '1',
          type: 'address',
          attributes: orderSummary.attributes.address
        }}
        isFilled={orderSummary.attributes.paymentMethod.source === PaymentSource.Amazon}
        readOnly={!orderSummary.attributes.isPaymentChangeable}
        onSwitchToAdyen={onSwitchToAdyen}
        onSubmit={onSubmit}
        isSaving={mode === Mode.Saving}
      />
      <OrderDetailsShippingAddressConfirmation
        show={mode === Mode.Switching}
        onClose={() => setMode(Mode.Resting)}
        onProceed={onClickProceed}
      />
    </>
  );
};
